import { computed, inject } from 'vue';
import { queryOptions, useQuery } from '@tanstack/vue-query';
import api from '@/api';
import { parseHTMLEntity } from '@/helpers/parse-entity';
import { customQueryClient } from '@/config/query-client';
import type { Consent, Me } from '@/api/queries/me/types/me';
import type { GetAppsUrls } from '@/plugins/env-urls/types';

async function fetchMe() {
  const response = await api<Me>({
    method: 'get',
    url: '/me',
  });

  return response;
}

export const meQueryOptions = queryOptions({
  queryKey: ['me'],
  queryFn: fetchMe,
});

export default function useMeQuery() {
  const { data: me, refetch, suspense: meSuspense } = useQuery(meQueryOptions, customQueryClient);
  const appUrl = inject('appUrl') as GetAppsUrls;

  // Auth user properties
  const auth = computed(() => me.value?.user);
  const avatarIsLoaded = computed(() => auth.value?.branding?.avatar?.includes('user/'));
  const currentName = computed(() =>
    auth.value?.first_name && auth.value?.last_name
      ? `${auth.value.first_name} ${auth.value.last_name}`
      : '',
  );

  const fullName = computed(() => parseHTMLEntity(currentName.value));

  // Me properties
  const isPoleEmploi = computed(() => {
    return me.value?.is_pole_emploi;
  });
  const permissions = computed(() => me.value?.permissions);
  const getConsent = (type: Consent['type']): Consent => me.value?.consent?.[type];

  const sharepools = computed(() => me.value?.sharepools);

  const isBrainDone = computed(() => me.value?.user.is_brain_done);
  const isDriveDone = computed(() => me.value?.user.is_drive_done);
  const isShapeDone = computed(() => me.value?.user.is_shape_done);

  if (me.value?.permissions?.includes('cst_na')) {
    window.location.assign(`${appUrl('auth_url')}/terms-of-use`);
  }

  return {
    auth,
    avatarIsLoaded,
    fullName,
    isPoleEmploi,
    me,
    sharepools,
    permissions,
    isBrainDone,
    isDriveDone,
    isShapeDone,
    getConsent,
    refetch,
    meSuspense,
  };
}
